import type { StatusDistribution } from '@/api/types/compliance-dashboard.types';

// Slice 2 (BI): control status distribution as proportional horizontal bars. Replaces the prior
// donut — the 8-state assignment lifecycle reads more clearly as labelled bars. Real state only.
interface ControlStatusBarsProps {
  data: StatusDistribution[];
  isLoading: boolean;
}

// Canonical lifecycle order + display label + bar colour for each assignment status.
const STATUS_META: Record<string, { label: string; color: string; order: number }> = {
  unassigned: { label: 'Unassigned', color: '#5C7189', order: 0 },
  assigned: { label: 'Assigned', color: '#3E5F86', order: 1 },
  in_progress: { label: 'In progress', color: '#D7A94A', order: 2 },
  submitted: { label: 'Submitted', color: '#4FA8E8', order: 3 },
  sent_back: { label: 'Sent back', color: '#C2453F', order: 4 },
  implemented: { label: 'Implemented', color: '#4A9E78', order: 5 },
  compensating_control: { label: 'Compensating', color: '#4CAF7D', order: 6 },
  not_applicable: { label: 'Not applicable', color: '#8E7BB8', order: 7 },
};

export function ControlStatusBars({ data, isLoading }: ControlStatusBarsProps) {
  const total = data.reduce((s, r) => s + r.count, 0);
  const rows = [...data]
    .filter((r) => r.count > 0)
    .sort((a, b) => (STATUS_META[a.status]?.order ?? 99) - (STATUS_META[b.status]?.order ?? 99));

  return (
    <div
      className="flex min-w-0 flex-col gap-[11px] rounded-xl border border-border-subtle bg-bg-surface p-[14px_16px_13px]"
      data-testid="control-status-bars"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-semibold text-text-primary">Control status distribution</span>
      </div>
      {isLoading || rows.length === 0 ? (
        <p className="text-[11.5px] text-text-secondary">
          {isLoading ? 'Loading…' : 'No controls in scope yet.'}
        </p>
      ) : (
        <div className="flex flex-col gap-[9px]">
          {rows.map((r) => {
            const meta = STATUS_META[r.status] ?? { label: r.status, color: '#5C7189', order: 99 };
            const pct = total > 0 ? (r.count / total) * 100 : 0;
            return (
              <div className="flex items-center gap-[11px]" key={r.status} data-testid="status-row">
                <span className="flex-[0_0_104px] truncate text-right text-[11.5px] text-text-muted">
                  {meta.label}
                </span>
                <span className="flex h-[15px] min-w-0 flex-[1_1_auto] overflow-hidden rounded-[3px] bg-[#0D1626]">
                  <span className="block h-full" style={{ width: `${pct}%`, background: meta.color }} />
                </span>
                <span className="flex-[0_0_52px] text-right text-[11.5px] font-semibold tabular-nums text-text-muted">
                  {r.count}
                </span>
              </div>
            );
          })}
        </div>
      )}
      <span className="mt-auto border-t border-[#1B2C43] pt-[9px] text-[11.5px] leading-[1.55] text-text-secondary">
        Status counts show where work currently stands.
      </span>
    </div>
  );
}
